import { Home, Mail, MapPin, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProfileData } from '../hooks/useProfileData';

interface PublicProfileComplianceFooterProps {
  profile: ProfileData;
  tone?: 'warm' | 'slate' | 'tech';
}

const toneClasses = {
  warm: {
    root: 'border-[#22201d]/10 bg-[#f5f1e9] text-[#716c65]',
    heading: 'text-[#22201d]',
    border: 'border-[#22201d]/10',
    link: 'text-[#7c542d] hover:text-[#22201d] focus:ring-[#9a6b38]'
  },
  slate: {
    root: 'border-white/10 bg-slate-950 text-slate-400',
    heading: 'text-white',
    border: 'border-white/10',
    link: 'text-amber-300 hover:text-amber-200 focus:ring-amber-300'
  },
  tech: {
    root: 'border-white/10 bg-[#050812] text-slate-400',
    heading: 'text-white',
    border: 'border-white/10',
    link: 'text-cyan-300 hover:text-cyan-200 focus:ring-cyan-300'
  }
} as const;

function privacyPolicyUrl(website?: string): string | undefined {
  if (!website) {
    return undefined;
  }

  try {
    const url = new URL(website);
    if (url.protocol !== 'https:') {
      return undefined;
    }
    return new URL('/privacy-policy.html', url.origin).toString();
  } catch {
    return undefined;
  }
}

function legalOfficerName(profile: ProfileData): string {
  return `${profile.firstName} ${profile.lastName}`.trim() || 'Loan Officer';
}

function companyLicenseLabel(state: string, licenseNumber: string): string {
  if (state.toUpperCase() === 'CA') {
    return `California DFPI license #${licenseNumber}`;
  }
  return `${state.toUpperCase()} license #${licenseNumber}`;
}

export function PublicProfileComplianceFooter({
  profile,
  tone = 'slate'
}: PublicProfileComplianceFooterProps) {
  const company = profile.companyInfo;
  const styles = toneClasses[tone];
  const privacyUrl = privacyPolicyUrl(company.website);
  const companyLicenses = company.licenses.filter((license) => license.state && license.licenseNumber);

  if (!company.complianceReady) {
    return null;
  }

  return (
    <footer role="contentinfo" className={cn('border-t py-10 sm:py-12', styles.root)}>
      <div className="mx-auto max-w-6xl px-5 text-xs leading-5 sm:px-8">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className={cn('text-sm font-semibold', styles.heading)}>{company.legalName}</p>
            <p className="mt-2">Company NMLS #{company.nmls}</p>
            {company.equalHousingLender && (
              <p className="mt-3 flex items-center gap-2">
                <Home className="h-4 w-4 shrink-0" aria-hidden="true" />
                Equal Housing Lender
              </p>
            )}
          </div>

          <div>
            <p className={cn('font-semibold', styles.heading)}>Corporate headquarters</p>
            <p className="mt-3 flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{company.address}</span>
            </p>
          </div>

          <div>
            <p className={cn('font-semibold', styles.heading)}>Company contact</p>
            <div className="mt-2 space-y-1">
              <a
                href={`tel:${company.phone.replace(/\D/g, '')}`}
                className={cn('flex min-h-9 items-center gap-2 rounded-sm focus:outline-none focus:ring-2', styles.link)}
              >
                <Phone className="h-4 w-4 shrink-0" aria-hidden="true" />
                {company.phone}
              </a>
              <a
                href={`mailto:${company.email}`}
                className={cn('flex min-h-9 items-center gap-2 break-all rounded-sm focus:outline-none focus:ring-2', styles.link)}
              >
                <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                {company.email}
              </a>
            </div>
          </div>
        </div>

        {companyLicenses.length > 0 && (
          <div className={cn('mt-8 border-t pt-6', styles.border)}>
            <p className={cn('font-semibold', styles.heading)}>Company licenses</p>
            <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2" aria-label="Company state licenses">
              {companyLicenses.map((license) => (
                <li key={`${license.state}-${license.licenseNumber}`}>
                  {companyLicenseLabel(license.state, license.licenseNumber)}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={cn('mt-8 border-t pt-6', styles.border)}>
          <div className="max-w-4xl space-y-2">
            {company.disclaimers.map((disclaimer) => (
              <p key={disclaimer}>{disclaimer}</p>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <p>
              {legalOfficerName(profile)}{profile.nmls ? ` · Individual NMLS #${profile.nmls}` : ''}
            </p>
            <nav aria-label="Legal and licensing links" className="flex flex-wrap gap-x-5 gap-y-3">
              {privacyUrl && (
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn('rounded-sm underline underline-offset-4 focus:outline-none focus:ring-2', styles.link)}
                >
                  Privacy policy
                </a>
              )}
              <a
                href="https://www.nmlsconsumeraccess.org/"
                target="_blank"
                rel="noopener noreferrer"
                className={cn('rounded-sm underline underline-offset-4 focus:outline-none focus:ring-2', styles.link)}
              >
                NMLS Consumer Access
              </a>
            </nav>
          </div>

          <p className="mt-6">
            © {new Date().getFullYear()} {company.legalName}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
